import { getData, postData, putData, deleteData } from '../../../api/common';
import { getCurrentUser } from '../../../utils/common.util';

/**
 * Create a new financial aid program (Step 1)
 * @param {Object} programData - The program data
 * @returns {Promise<Object>} - Response with programId on success
 */
export const createAidProgram = async (programData) => {
  // Transform form data to API payload structure
  const payload = {
    name: programData.programName,
    description: programData.description || '',
    type: programData.aidType,
    amountType: programData.amountType,
    amount: parseFloat(programData.amount),
    academicYearId: parseInt(programData.academicYearId, 10),
    termId: programData.termId ? parseInt(programData.termId, 10) : null,
  };

  const authToken = getCurrentUser()?.authToken;
  const response = await postData(
    'financial-aid/programs/create',
    payload,
    authToken,
  );

  if (response.success) {
    return {
      success: true,
      programId: response.data?.id || response.data?.programId,
      message: 'Financial aid program created successfully.',
    };
  }

  return response;
};

/**
 * Update bill items linked to a program (Step 2)
 * @param {number|string} programId - The program ID
 * @param {Array} billItemsData - Array of { selectedBillItems: [], overrideAmounts: {} }
 * @returns {Promise<Object>} - Response on success
 */
export const updateBillItems = async (programId, billItemsData) => {
  const { selectedBillItems, overrideAmounts } = billItemsData;

  // Transform to API payload: [{ billingFeeId: number, overrideAmount: number | null }, ...]
  const payload = selectedBillItems.map((billingFeeId) => ({
    billingFeeId: parseInt(billingFeeId, 10),
    overrideAmount: overrideAmounts[billingFeeId]
      ? parseFloat(overrideAmounts[billingFeeId])
      : null,
  }));

  const authToken = getCurrentUser()?.authToken;
  const response = await putData(
    `financial-aid/programs/${programId}/add-bill-items`,
    payload,
    authToken,
  );

  if (response.success) {
    return {
      success: true,
      message: 'Bill items linked successfully.',
    };
  }

  return response;
};

/**
 * Assign students to a program (Step 3)
 * @param {number|string} programId - The program ID
 * @param {Object} studentsData - Object with assignedStudents array and overrideAmounts object
 * @returns {Promise<Object>} - Response on success
 */
export const assignStudents = async (programId, studentsData) => {
  const { assignedStudents, overrideAmounts } = studentsData;

  // Transform to API payload: [{ studentId: number, overrideAmount: number | null }, ...]
  const payload = assignedStudents.map((student) => ({
    studentId: student.studentId,
    overrideAmount: overrideAmounts[student.studentId]
      ? parseFloat(overrideAmounts[student.studentId])
      : null,
  }));

  const authToken = getCurrentUser()?.authToken;
  const response = await postData(
    `financial-aid/programs/${programId}/assign-students`,
    payload,
    authToken,
  );

  if (response.success) {
    return {
      success: true,
      message: 'Students assigned successfully.',
    };
  }

  return response;
};

/**
 * Remove a student from a financial aid program
 * @param {number|string} programId - The program ID
 * @param {number|string} studentId - The student ID to remove
 * @returns {Promise<Object>} - Response on success
 */
export const removeStudentFromProgram = async (programId, studentId) => {
  const authToken = getCurrentUser()?.authToken;
  const response = await deleteData(
    `financial-aid/programs/${programId}/students/${studentId}`,
    authToken,
  );

  if (response.success) {
    return {
      success: true,
      message: 'Student removed from program successfully.',
    };
  }

  return response;
};

/**
 * Remove a bill item from a financial aid program
 * @param {number|string} programId - The program ID
 * @param {number|string} billItemId - The bill item ID to remove
 * @returns {Promise<Object>} - Response on success
 */
export const removeBillItem = async (programId, billItemId) => {
  const authToken = getCurrentUser()?.authToken;
  const response = await deleteData(
    `financial-aid/programs/${programId}/remove-bill-items/${billItemId}`,
    authToken,
  );

  if (response.success) {
    return {
      success: true,
      message: 'Bill item removed successfully.',
    };
  }

  return response;
};

/**
 * Get a single financial aid program by ID (for edit page)
 * @param {number|string} programId - The program ID
 * @returns {Promise<Object>} - Response with program data
 */
export const getAidProgram = async (programId) => {
  const authToken = getCurrentUser()?.authToken;
  const response = await getData(
    `financial-aid/programs/${programId}`,
    authToken,
  );

  if (response.success) {
    return {
      success: true,
      program: response.data,
    };
  }

  return response;
};

/**
 * Get all financial aid programs with optional filters
 * @param {Object} filters - Filter options
 * @param {number|string} filters.academicYearId - Required academic year ID
 * @param {number|string} [filters.termId] - Optional term ID
 * @param {string} [filters.type] - Optional aid type (Scholarship, Discount, Waiver)
 * @param {boolean} [filters.isActive] - Optional filter by active status
 * @returns {Promise<Object>} - Response with programs array
 */
export const getAidPrograms = async (filters) => {
  const { academicYearId, termId, type, isActive } = filters;

  // Build query string
  const params = new URLSearchParams();
  params.append('academicYearId', academicYearId);

  if (termId) {
    params.append('termId', termId);
  }
  if (type) {
    params.append('type', type);
  }
  if (isActive !== undefined && isActive !== '') {
    params.append('isActive', isActive);
  }

  const authToken = getCurrentUser()?.authToken;
  const response = await getData(
    `financial-aid/programs?${params.toString()}`,
    authToken,
  );

  if (response.success) {
    return {
      success: true,
      programs: response.data,
    };
  }

  return response;
};

/**
 * Get bill items linked to a program (for edit page)
 * @param {number|string} programId - The program ID
 * @returns {Promise<Object>} - Response with linked bill items
 */
export const getProgramBillItems = async (programId) => {
  const authToken = getCurrentUser()?.authToken;
  const response = await getData(
    `financial-aid/programs/${programId}/bill-items`,
    authToken,
  );

  if (response.success) {
    return {
      success: true,
      billItems: response.data,
    };
  }

  return response;
};

/**
 * Get students assigned to a program
 * @param {number|string} programId - The program ID
 * @returns {Promise<Object>} - Response with assigned students
 */
export const getProgramStudents = async (programId) => {
  const authToken = getCurrentUser()?.authToken;
  const response = await getData(
    `financial-aid/programs/${programId}/students`,
    authToken,
  );

  if (response.success) {
    return {
      success: true,
      students: response.data,
    };
  }

  return response;
};

/**
 * Update an existing financial aid program (for edit page)
 * @param {number|string} programId - The program ID
 * @param {Object} programData - The updated program data
 * @returns {Promise<Object>} - Response on success
 */
export const updateAidProgram = async (programId, programData) => {
  const payload = {
    name: programData.programName,
    description: programData.description || '',
    type: programData.aidType,
    amountType: programData.amountType,
    amount: parseFloat(programData.amount),
    academicYearId: parseInt(programData.academicYearId, 10),
    termId: programData.termId ? parseInt(programData.termId, 10) : null,
  };

  const authToken = getCurrentUser()?.authToken;
  const response = await putData(
    `financial-aid/programs/${programId}/update`,
    payload,
    authToken,
  );

  if (response.success) {
    return {
      success: true,
      message: 'Financial aid program updated successfully.',
    };
  }

  return response;
};

/**
 * Roll over financial aid programs from source term to target term
 * @param {Object} rolloverData - The rollover configuration
 * @param {number|string} rolloverData.sourceTermId - Source term ID
 * @param {number|string} rolloverData.targetTermId - Target term ID (0 if none)
 * @param {number|string} rolloverData.targetAcademicYearId - Target academic year ID
 * @param {boolean} rolloverData.copyBillItems - Whether to copy bill items
 * @param {boolean} rolloverData.copyStudents - Whether to copy students
 * @returns {Promise<Object>} - Response on success
 */
export const rolloverAidPrograms = async (rolloverData) => {
  const payload = {
    sourceTermId: parseInt(rolloverData.sourceTermId, 10),
    targetTermId: parseInt(rolloverData.targetTermId, 10),
    targetAcademicYearId: parseInt(rolloverData.targetAcademicYearId, 10),
    copyBillItems: rolloverData.copyBillItems,
    copyStudents: rolloverData.copyStudents,
  };

  const authToken = getCurrentUser()?.authToken;
  const response = await postData(
    'financial-aid/programs/rollover',
    payload,
    authToken,
  );

  if (response.success) {
    return {
      success: true,
      message: 'Programs rolled over successfully.',
    };
  }

  return response;
};
